import _ from 'lodash'
import DataSourceWidgetModel from '../data-source-widget-model'
import NotesWidgetView from './notes-widget-view'
import ScorecardNode from 'scoreboard/scorecards/scorecard-node'
import Initiative from 'scoreboard/initiatives/initiative'

var NotesWidget = DataSourceWidgetModel.extend({
	viewCls: NotesWidgetView,

	containerDefaults: {
		// #20817 use minimum width to be two notes wide
		width: 446,
		height: 340
	},

	section: function () {
		return this.properties.get('section')
	},

	/**
	 * Returns a "raw" version of the domain object for which we're displaying
	 * notes. By "raw" we mean just the raw data that's not wrapped in a
	 * Backbone model
	 */
	rawDomainObject: function () {
		var section = this.section()
		var domainObjectId = this.properties.get('domainObjectId')
		var domainObject = _.cloneDeep(this.data[section]?.(domainObjectId))
		return domainObject
	},

	/**
	 * Returns the Model for the initiative or scorecard item for which we are
	 * displaying notes
	 *
	 * @return {ScorecardNode|Initiative}
	 */
	domainObject: function () {
		var rawDomainObject = this.rawDomainObject()
		if (!rawDomainObject) {
			return null
		}

		var section = this.section()
		if (section === 'scorecards') {
			if (!this.properties.get('showNoteDescendants')) {
				// if the scorecard node is in another widget with showNoteDescendants=true,
				// then the notes will be populated. We want to remove them, though, since
				// this widget is showNoteDescendants=false
				rawDomainObject.notes = rawDomainObject.notes?.filter(note => {
					if (rawDomainObject.type === 'linked' && rawDomainObject.useSourceNotes) {
						// #17896 if linked and using source notes, treat the source
						// node's notes as its own
						return note.domainId == rawDomainObject.sourceId
					}
					return note.domainId == rawDomainObject.id
				})
			}

			return new ScorecardNode(rawDomainObject)
		} else if (section === 'initiatives') {
			return new Initiative(rawDomainObject)
		}
	},

	validShowProperties: function () {
		var section = this.section()
		var props = []

		props.push({ property: 'showLabel', label: 'dashboards.display.title' })
		props.push({ property: 'showControls', label: 'dashboards.display.noteControls'})
		props.push({ property: 'showModifyDate', label: 'dashboards.display.notePostedDate'})
		props.push({ property: 'showReplies', label: 'dashboards.display.noteReplies'})
		props.push({ property: 'showAuthor', label: 'dashboards.display.noteAuthor'})
		props.push({ property: 'showNoteAppliesTo', label: 'dashboards.display.notePeriod'})

		if (section === 'scorecards') {
			// Only showing the "about" toggle for scorecards, because only scorecard notes have the
			// option to show descendant notes. Thus, showing the initiative name on each note would
			// be unnecessary; it makes more sense to just show it once in the title bar (showLabel)
			props.push({ property: 'showNoteAbout', label: 'dashboards.display.noteScorecardNode'})
			props.push({ property: 'showNoteDescendants', label: 'dashboards.display.notesFromDescendants'})
		}

		return props
	},

	/**
	 * Returns the "showX" property names that should currently be available for
	 * configuration, taking into account things like the displayMode
	 */
	visibleShowPropertyNames() {
		let section = this.section()
		let displayMode = this.properties.get('displayMode')

		let visible = [ 'showLabel', 'showControls', 'showModifyDate', 'showReplies' ]

		if (section === 'scorecards') {
			visible.push('showNoteDescendants')
		}

		if (displayMode === 'full') {
			visible.push('showAuthor')
			visible.push('showNoteAbout')
			visible.push('showNoteAppliesTo')
		}

		return visible
	},

	multiSettableProps: function () {
		return _.map(this.validShowProperties(), 'property')
			.concat([ 'background', 'displayMode', 'fontSize' ])
	},

	/**
	 * Returns valid showX properties for the note widget's data source's
	 * domainType
	 *
	 * @override
	 */
	validShowPropertyKeys () {
		return showPropsByDomainType[this.domainType() ?? 'none'] || []
	}
})

const showPropsForScorecardsAndInititatives = [
	'showLabel', 'showControls', 'showModifyDate', 'showReplies',
	'showAuthor', 'showNoteAppliesTo']
const showPropsForScorecardNotes = ['showNoteAbout', 'showNoteDescendants']
const showPropsByDomainType = {
	'scorecard-item' : showPropsForScorecardsAndInititatives
		.concat(showPropsForScorecardNotes),
	initiative: showPropsForScorecardsAndInititatives,
	none: showPropsForScorecardsAndInititatives
}

export default NotesWidget

NotesWidget.__filename__ = 'dashboards/widgets/notes/notes-widget-model.js'
